/**
 * Sanitize null values from database records before passing to Zod-validated forms.
 *
 * Prisma returns `null` for optional fields, but Zod schemas (via `optStr()`)
 * typically only accept `string | undefined | ""`. This utility converts all
 * `null` values to `undefined` recursively so the data passes validation.
 */

/**
 * Chuyển đổi tất cả giá trị `null` thành `undefined` đệ quy.
 * Dùng trước khi truyền dữ liệu DB vào form defaultValues hoặc form.reset().
 *
 * @example
 * const dbData = await trpcClient.products.getById.query({ id });
 * const safeData = sanitizeNullsDeep(dbData);
 * form.reset(safeData);
 */
export function sanitizeNullsDeep<T>(obj: T): T {
    if (obj === null) return undefined as any;
    if (Array.isArray(obj)) return obj.map(sanitizeNullsDeep) as any;
    if (obj instanceof Date) return obj;
    if (typeof obj === 'object' && obj !== null) {
        const result: any = {};
        for (const [key, val] of Object.entries(obj)) {
            result[key] = sanitizeNullsDeep(val);
        }
        return result;
    }
    return obj;
}

/**
 * Chuẩn hóa các trường số trong payload trước khi gửi lên API.
 * - Chuỗi số (vd: Decimal trả về dạng "5000.00" từ DB) được ép sang number.
 * - Chuỗi rỗng/null/undefined bị loại khỏi payload (không gửi lên).
 * - Giá trị không phải số hợp lệ cũng bị loại bỏ để tránh gửi NaN.
 */
export function normalizeNumericFields<T extends Record<string, any>>(
    payload: T,
    numericKeys: readonly string[],
): T {
    const result: Record<string, any> = { ...payload };
    for (const key of numericKeys) {
        const raw = result[key];
        if (raw === undefined) continue;
        if (raw === null || (typeof raw === 'string' && raw.trim() === '')) {
            delete result[key];
            continue;
        }
        const num = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''));
        if (Number.isFinite(num)) {
            result[key] = num;
        } else {
            delete result[key];
        }
    }
    return result as T;
}
